/*
 * 复制自 src/pages/mainWrapper/Header/GroupInfo.tsx
 * 改动：在团队列表顶部增加「个人空间」固定选项
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Input, Avatar, Modal } from 'antd'
import { DownOutlined, SearchOutlined } from '@ant-design/icons'
// 注：违反 my-space 规范跨界 import（已与负责人确认），原因是
// 顶部 Header 的加入/创建团队 Modal 是同一份组件，复用避免冗余
import CreateGroup from '../../../components/CreateGroup'
import JoinGroup from '../../../components/JoinGroup'
import { useAppSelector, useVip } from '../../../../app/hooks'
import sVipGroupBadge from '../../../../images/header/svip_group.png'
import vipGroupBadge from '../../../../images/header/vip_group.png'
import vVipGroupBadge from '../../../../images/header/vVip_group.png'
import notVipGroupBadge from '../../../../images/header/not_vip_group.png'
import vipGroupIcon from '../../../../images/crown-vip-icon.png'
import personalVipIcon from '../../../../images/personal_vip.png'
import personalVipIconSmall from '../../../../images/icon_vip_personal.png'
import sVipGroupIcon from '../../../../images/crown-svip-icon.png'
import vVipGroupIcon from '../../../../images/crown-vVip-icon.png'
import groupSelected from '../../../../images/header/group_selected.png'
import sensors from '../../../../utils/sensors'
import CONFIG from '../../../../constants/config'
import { pauseAllDownloadingTasks } from '../../../../utils/downloadTaskManager'

const PERSONAL_SPACE_PATH = '/my-space/personal-space'

const isOnPersonalSpace = () => window.location.pathname.startsWith('/my-space')

const GroupAvatar = ({ avatar, name, color, className }) => {
  if (avatar && avatar !== '') {
    return <img className={className} src={avatar} alt="" />
  } else {
    return (
      <div className={className} style={{ backgroundColor: color }}>
        {name.substr(0, 2)}
      </div>
    )
  }
}

const ChooseGroup = ({
  visible,
  onTrack,
  onClickJoin,
  onClickCreate
}: {
  visible: boolean
  onTrack?: (clickItem: string) => void
  onClickJoin?: () => void
  onClickCreate?: () => void
}) => {
  const groups = useAppSelector((state) => state.main.user?.groups)
  const curGroup = useAppSelector((state) => state.group)
  const nickname =
    (useAppSelector((state) => state.main.user?.nickname) as string) || ''
  const headimgurl =
    (useAppSelector((state) => state.main.user?.headimgurl) as string) || ''
  const personalTotal = useAppSelector(
    (state: any) => state.mySpace?.personalSpace?.total ?? 0
  )
  const isSpaceOpen = useAppSelector(
    (state: any) => state.mySpace?.personalSpace?.isSpaceOpen ?? false
  )
  const isPersonalVip = useAppSelector(
    (state: any) => state.mySpace?.personalSpace?.isPersonalVip ?? false
  )
  const [groupName, setGroupName] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const blurTimerRef = useRef<number | null>(null)

  const cancelBlurTimer = () => {
    if (blurTimerRef.current !== null) {
      window.clearTimeout(blurTimerRef.current)
      blurTimerRef.current = null
    }
  }

  const handleFocus = () => {
    cancelBlurTimer()
    setIsFocused(true)
  }

  const handleBlur = () => {
    cancelBlurTimer()
    blurTimerRef.current = window.setTimeout(() => {
      setIsFocused(false)
      blurTimerRef.current = null
    }, 120)
  }

  useEffect(() => () => cancelBlurTimer(), [])

  const goPersonalSpace = useCallback(() => {
    if (onTrack) onTrack('switchGroup')
    sensors.track('web_workgroup_nav_menu_cli', {
      path: '切换个人空间',
      bool1: true,
      groupID: '',
      isGroupVIP: false,
      role: '',
      isAvailable: true,
      track_sign: 'aworkgroup.b4311.action.web_workgroup_nav_menu_cli',
      requirement_id: 608
    })
    window.location.href = PERSONAL_SPACE_PATH
  }, [onTrack])

  const changeGroup = useCallback(
    async (group) => {
      if (onTrack) onTrack('switchGroup')
      const currentGroupId = localStorage.getItem('group_id')
      if (currentGroupId && currentGroupId !== group.group_id) {
        await pauseAllDownloadingTasks(currentGroupId)
      }
      localStorage.setItem('photoExportDownloadedTasks', 'null')
      localStorage.setItem('photoExportTasks', 'null')
      sessionStorage.setItem('switching_group', 'true')
      localStorage.setItem('group_id', group.group_id)

      // 在个人空间页切换团队时，跳到团队照片页（避免 '/' 被 handleAfterLogin 误判为首次登录）
      if (isOnPersonalSpace()) {
        window.location.href = '/photo'
        return
      }

      if (/\/photo\/folder\/detail\/\d+/.test(window.location.pathname)) {
        return (window.location.href = '/photo/folders')
      }

      window.location.reload()
    },
    [onTrack]
  )

  if (!visible) return null

  return (
    <div className="pop-choose-group-wrap">
      <div className="pop-choose-group">
        <div
          className={`search-group-wrap${
            isFocused ? ' search-group-wrap--focused' : ''
          }`}
        >
          <Input
            className="search-group"
            prefix={<SearchOutlined style={{ color: '#848484' }} />}
            placeholder="搜索"
            value={groupName}
            allowClear
            onChange={(e) => setGroupName(e.target.value.trim())}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {!isFocused && (
            <div className="group-action-buttons">
              <button
                type="button"
                className="group-action-btn group-action-btn--join"
                onClick={() => onClickJoin && onClickJoin()}
              >
                加入团队
              </button>
              <button
                type="button"
                className="group-action-btn group-action-btn--create"
                onClick={() => onClickCreate && onClickCreate()}
              >
                创建团队
              </button>
            </div>
          )}
        </div>

        <div className="groups">
          {/* 个人空间固定选项（isOpen=true 时才显示） */}
          {isSpaceOpen && (
            <div
              className="group"
              onClick={goPersonalSpace}
              style={{ borderBottom: '1px solid #f0f0f0' }}
            >
              {headimgurl ? (
                <img
                  className="group-avatar"
                  src={headimgurl}
                  style={{ objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                  alt=""
                />
              ) : (
                <div
                  className="group-avatar"
                  style={{
                    backgroundColor: '#0070F4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#fff',
                    borderRadius: 4,
                    flexShrink: 0
                  }}
                >
                  {nickname ? nickname.slice(0, 2) : '我'}
                </div>
              )}
              <div className="group-name-wrapper">
                <div className="group-name">
                  <span>{nickname}的空间</span>
                  {isPersonalVip && (
                    <img
                      className="vip-group-icon"
                      src={personalVipIconSmall}
                      alt=""
                    />
                  )}
                  <span
                    style={{
                      fontSize: 12,
                      color: '#666',
                      background: '#F5F5F5',
                      borderRadius: 4,
                      padding: '2px 4px',
                      marginLeft: 6,
                      fontWeight: 400
                    }}
                  >
                    仅自己可见
                  </span>
                </div>
                <div className="group-id" style={{ color: 'rgba(0,0,0,0.45)' }}>
                  {personalTotal}张照片
                </div>
              </div>
              {isOnPersonalSpace() && (
                <img className="group-selected" src={groupSelected} />
              )}
            </div>
          )}

          {/* 团队列表 */}
          {(groups || [])
            .filter((group) => group.group_name?.includes(groupName))
            .map((group, index) => (
              <div
                key={index}
                className="group"
                onClick={() => changeGroup(group)}
              >
                <GroupAvatar
                  key={index}
                  className="group-avatar"
                  avatar={group.avatar}
                  name={group.group_name}
                  color={group.group_color}
                />
                <div className="group-name-wrapper">
                  <div className="group-name">
                    <span>{group.group_name}</span>
                    {group.is_vip && (
                      <img
                        className="vip-group-icon"
                        src={
                          group.vip_kind === CONFIG.VIP_KIND.VVIP
                            ? vVipGroupIcon
                            : group.vip_kind === CONFIG.VIP_KIND.SVIP
                              ? sVipGroupIcon
                              : vipGroupIcon
                        }
                        alt=""
                      />
                    )}
                  </div>
                  <div className="group-id">
                    团队号：
                    {group.group_lucky_id || group.invite_id}
                  </div>
                </div>
                {curGroup.groupId === group.group_id &&
                  !isOnPersonalSpace() && (
                    <img className="group-selected" src={groupSelected} />
                  )}
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

const GroupInfo = ({ onTrack }: { onTrack?: (clickItem: string) => void }) => {
  const [createGroupModuleVisible, setCreateGroupModuleVisible] =
    useState(false)
  const [joinGroupModuleVisible, setJoinGroupModuleVisible] = useState(false)
  const [createFromPlace, setCreateFromPlace] = useState('navMenuMySpace')
  const [joinFromPlace, setJoinFromPlace] = useState('navMenuMySpace')

  const handleCreateGroupClick = (fromPlace = 'navMenuMySpace') => {
    if (onTrack) onTrack('clickCreate')
    setCreateFromPlace(fromPlace)
    setCreateGroupModuleVisible(true)
  }
  const handleJoinGroupClick = (fromPlace = 'navMenuMySpace') => {
    if (onTrack) onTrack('clickJoin')
    setJoinFromPlace(fromPlace)
    setJoinGroupModuleVisible(true)
  }

  const group = useAppSelector((state) => state.group)
  const nickname = useAppSelector(
    (state) => (state.main.user?.nickname as string) || ''
  )
  const headimgurl = useAppSelector(
    (state) => (state.main.user?.headimgurl as string) || ''
  )
  const [popChangeGroupVisible, setPopChangeGroupVisible] = useState(false)
  const { isVip, isSVip, isVVip } = useVip()
  const onPersonalSpace = isOnPersonalSpace()
  const isPersonalVip = useAppSelector(
    (s: any) => s.mySpace?.personalSpace?.isPersonalVip ?? false
  )
  const vipStatusLoaded = useAppSelector(
    (s: any) => s.mySpace?.personalSpace?.vipStatusLoaded ?? false
  )
  const showVipBadge = vipStatusLoaded && isPersonalVip
  const isSpaceOpen = useAppSelector(
    (s: any) => s.mySpace?.personalSpace?.isSpaceOpen ?? false
  )

  const mouseMoveEvent = useCallback((e) => {
    if (e.clientX > 335) {
      setPopChangeGroupVisible(false)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('mousemove', mouseMoveEvent)
    return () => {
      window.removeEventListener('mousemove', mouseMoveEvent)
    }
  }, [])

  return (
    <div
      className="header-group-info"
      onMouseEnter={() => {
        sensors.track('web_workgroup_nav_menu_cli', {
          path: 'switchGroup',
          isGroupVIP: group.isVip,
          bool1: isSpaceOpen
        })
        setPopChangeGroupVisible(true)
      }}
    >
      {onPersonalSpace ? (
        /* 个人空间：单行结构，头像 + 名称 + 箭头，完全独立于团队结构 */
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            shape="square"
            size={34}
            src={headimgurl || undefined}
            style={{ borderRadius: 4, flexShrink: 0 }}
          >
            {!headimgurl && (nickname ? nickname.slice(0, 2) : '我')}
          </Avatar>
          <div
            style={{
              marginLeft: 4,
              height: 38,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center'
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                lineHeight: '20px',
                color: '#000',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{nickname}的空间</span>
              <DownOutlined
                style={{ fontSize: 12, color: '#000', marginLeft: 4 }}
              />
            </div>
            {showVipBadge && (
              <div
                style={{ height: 20, display: 'flex', alignItems: 'center' }}
              >
                <img
                  src={personalVipIcon}
                  alt=""
                  style={{ height: 20, width: 'auto' }}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        /* 团队空间：保持原有两行结构不变 */
        <>
          <GroupAvatar
            avatar={group.avatar}
            name={group.groupName}
            color={group.color}
            className="group-avatar"
          />
          <div className="group-name-wrap">
            <div className="group-name">
              <p>{group.groupName}</p>
              <DownOutlined />
            </div>
            <img
              className="group-vip-badge"
              src={
                isVVip
                  ? vVipGroupBadge
                  : isSVip
                    ? sVipGroupBadge
                    : isVip
                      ? vipGroupBadge
                      : notVipGroupBadge
              }
              alt=""
            />
          </div>
        </>
      )}
      <ChooseGroup
        visible={popChangeGroupVisible}
        onTrack={onTrack}
        onClickJoin={() => {
          setPopChangeGroupVisible(false)
          handleJoinGroupClick('navMenuMySpace')
        }}
        onClickCreate={() => {
          setPopChangeGroupVisible(false)
          handleCreateGroupClick('navMenuMySpace')
        }}
      />
      <Modal
        open={createGroupModuleVisible}
        footer={null}
        closable={true}
        mask={{ closable: true }}
        width={394}
        style={{ top: '20%' }}
        onCancel={() => setCreateGroupModuleVisible(false)}
      >
        <CreateGroup
          fromPlace={createFromPlace}
          onGotoJoin={() => {
            setCreateGroupModuleVisible(false)
            handleJoinGroupClick('createGroup')
          }}
        />
      </Modal>
      <Modal
        open={joinGroupModuleVisible}
        footer={null}
        closable={true}
        mask={{ closable: true }}
        width={394}
        className="join-group-modal"
        style={{ top: '20%' }}
        styles={{ body: { padding: 0, height: 516 } }}
        onCancel={() => setJoinGroupModuleVisible(false)}
        destroyOnHidden
      >
        <JoinGroup
          fromPlace={joinFromPlace}
          onClose={() => setJoinGroupModuleVisible(false)}
          onGotoCreate={() => {
            setJoinGroupModuleVisible(false)
            handleCreateGroupClick('joinGroup')
          }}
        />
      </Modal>
    </div>
  )
}

export default GroupInfo
